import { useState } from "react";
import { EnvelopeSimple, BellSimple } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import client from "../lib/api";
import { toast } from "sonner";

const KINDS_BY_ROLE = {
  admin: [
    { kind: "booking", label: "New shoot booked", desc: "A new shoot is scheduled" },
    { kind: "message", label: "New message", desc: "Someone messages you on a shoot" },
  ],
  employee: [
    { kind: "message", label: "New message", desc: "Someone messages you on a shoot" },
  ],
  agent: [
    { kind: "booking", label: "Shoot booked", desc: "Your shoot is scheduled" },
    { kind: "message", label: "New message", desc: "Someone messages you on a shoot" },
    { kind: "deliverables", label: "Deliverables ready", desc: "Your photos are ready to view" },
    { kind: "lead", label: "New lead", desc: "An inquiry or tour request on your property page" },
  ],
  photographer: [
    { kind: "assignment", label: "Shoot assigned", desc: "You're assigned or claim a shoot" },
    { kind: "message", label: "New message", desc: "Someone messages you on a shoot" },
    { kind: "deadline", label: "Delivery deadlines", desc: "A reminder before, and if a deadline is missed" },
    { kind: "payout", label: "Payout receipts", desc: "You've been paid" },
  ],
};
KINDS_BY_ROLE.pilot = KINDS_BY_ROLE.photographer;

export default function NotificationPrefs() {
  const { user, setUser } = useAuth();
  const kinds = KINDS_BY_ROLE[user.role] || KINDS_BY_ROLE.agent;
  const [prefs, setPrefs] = useState(() => {
    const base = {};
    kinds.forEach((k) => { base[k.kind] = (user.notify_prefs || {})[k.kind] ?? true; });
    return base;
  });
  const [saving, setSaving] = useState(false);

  const toggle = (kind) => setPrefs((p) => ({ ...p, [kind]: !p[kind] }));

  const save = async () => {
    setSaving(true);
    try {
      const merged = { ...(user.notify_prefs || {}), ...prefs };
      const { data } = await client.patch("/profile", { notify_prefs: merged });
      setUser(data);
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save preferences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="notification-prefs">
      <div className="lux-card rounded-[5px] p-6">
        <p className="eyebrow text-[#B08D57]">Delivery</p>
        <h2 className="font-display text-2xl text-[#2C2626] mt-1">Email notifications</h2>
        <p className="text-sm text-[#6B6660] mt-2 flex items-center gap-1.5">
          <BellSimple size={15} weight="fill" className="text-[#B08D57]" />
          Everything always appears in your in-portal bell. Turn a switch on to also get an email.
        </p>

        <div className="mt-6 divide-y divide-[#F0ECE2]">
          {kinds.map((k) => (
            <div key={k.kind} className="flex items-center justify-between py-4" data-testid={`pref-row-${k.kind}`}>
              <div className="min-w-0 pr-4">
                <p className="text-sm font-semibold text-[#2C2626]">{k.label}</p>
                <p className="text-xs text-[#8A8681] mt-0.5">{k.desc}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <EnvelopeSimple size={16} className={prefs[k.kind] ? "text-[#4A0000]" : "text-[#C9C4BA]"} weight="duotone" />
                <Switch
                  data-testid={`pref-switch-${k.kind}`}
                  checked={!!prefs[k.kind]}
                  onCheckedChange={() => toggle(k.kind)}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <Button data-testid="pref-save" onClick={save} disabled={saving}
            className="bg-[#4A0000] hover:bg-[#630000] text-white px-6 rounded-[5px]">
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </div>
    </div>
  );
}
